import type { Triangulation, Vec2 } from '../../geo/types';
import type { LabelBoundaries, VertexLabeller } from './densify';
import { clearestCornerProbes, probeInto, type CornerProbe } from './corners';

type Label = string | null;
type Edge = readonly [number, number];

// Working buffers of one refinement; triangles are only ever appended or retired, never reused.
interface Work {
  readonly positions: number[];
  readonly corners: number[];
  readonly alive: boolean[];
  readonly edges: Map<number, number[]>;
  readonly labels: Label[];
  readonly labeller: VertexLabeller;
}

// Vertex counts stay far below 2^26, so both indices of an edge fit one exact double.
const KEY_BASE = 2 ** 26;

function edgeKey(a: number, b: number): number {
  return a < b ? a * KEY_BASE + b : b * KEY_BASE + a;
}

function pointAt(work: Work, v: number): Vec2 {
  return [work.positions[v * 2]!, work.positions[v * 2 + 1]!];
}

function cornersOf(work: Work, t: number): [number, number, number] {
  return [work.corners[t * 3]!, work.corners[t * 3 + 1]!, work.corners[t * 3 + 2]!];
}

function lengthSq(work: Work, a: number, b: number): number {
  const dx = work.positions[b * 2]! - work.positions[a * 2]!;
  const dy = work.positions[b * 2 + 1]! - work.positions[a * 2 + 1]!;
  return dx * dx + dy * dy;
}

// Ties go to the smaller key, so two triangles agree on which of their edges is longest.
function longestEdge(work: Work, t: number): Edge {
  let best: Edge = [work.corners[t * 3]!, work.corners[t * 3 + 1]!];
  let bestLength = lengthSq(work, best[0], best[1]);
  for (let k = 1; k < 3; k++) {
    const edge: Edge = [work.corners[t * 3 + k]!, work.corners[t * 3 + ((k + 1) % 3)]!];
    const length = lengthSq(work, edge[0], edge[1]);
    const longer =
      length > bestLength || (length === bestLength && edgeKey(...edge) < edgeKey(...best));
    if (longer) [best, bestLength] = [edge, length];
  }
  return best;
}

function addTriangle(work: Work, corners: readonly [number, number, number]): number {
  const t = work.alive.length;
  work.corners.push(corners[0], corners[1], corners[2]);
  work.alive.push(true);
  for (let k = 0; k < 3; k++) {
    const key = edgeKey(corners[k]!, corners[(k + 1) % 3]!);
    const sharing = work.edges.get(key);
    if (sharing) sharing.push(t);
    else work.edges.set(key, [t]);
  }
  return t;
}

function retireTriangle(work: Work, t: number): void {
  work.alive[t] = false;
  for (let k = 0; k < 3; k++) {
    const key = edgeKey(work.corners[t * 3 + k]!, work.corners[t * 3 + ((k + 1) % 3)]!);
    const sharing = work.edges.get(key) ?? [];
    sharing.splice(sharing.indexOf(t), 1);
    if (sharing.length === 0) work.edges.delete(key);
  }
}

// Every triangle around a new midpoint holds it, so each gives it one corner probe; the first of the
// clearest wins, as in clearestCornerProbes.
function labelVertex(work: Work, v: number, around: readonly number[]): void {
  let best: CornerProbe | undefined;
  for (const t of around) {
    const [a, b, c] = cornersOf(work, t);
    const [p, q, r] = a === v ? [a, b, c] : b === v ? [b, c, a] : [c, a, b];
    const probe = probeInto(
      pointAt(work, p),
      pointAt(work, q),
      pointAt(work, r),
      work.labeller.reach
    );
    if (!best || probe.clearance > best.clearance) best = probe;
  }
  work.labels[v] = best ? work.labeller.labelAt(pointAt(work, v), best) : null;
}

// The corners of a triangle on an edge, in its winding, starting at the edge: [p, q] is the edge and r
// the apex across it.
function aroundEdge(work: Work, t: number, key: number): [number, number, number] {
  const c = cornersOf(work, t);
  const i = [0, 1, 2].find((k) => edgeKey(c[k]!, c[(k + 1) % 3]!) === key)!;
  return [c[i]!, c[(i + 1) % 3]!, c[(i + 2) % 3]!];
}

// Both triangles on the edge are halved at one new midpoint, keeping their winding, so the mesh stays
// conforming.
function bisectEdge(work: Work, a: number, b: number): readonly number[] {
  const m = work.positions.length / 2;
  work.positions.push(
    (work.positions[a * 2]! + work.positions[b * 2]!) / 2,
    (work.positions[a * 2 + 1]! + work.positions[b * 2 + 1]!) / 2
  );
  const key = edgeKey(a, b);
  const created = [...(work.edges.get(key) ?? [])].flatMap((t) => {
    const [p, q, r] = aroundEdge(work, t, key);
    retireTriangle(work, t);
    return [addTriangle(work, [p, m, r]), addTriangle(work, [m, q, r])];
  });
  labelVertex(work, m, created);
  return created;
}

function singlePrecisionAt(work: Work, v: number): Vec2 {
  return [Math.fround(work.positions[v * 2]!), Math.fround(work.positions[v * 2 + 1]!)];
}

function facesUp(a: Vec2, b: Vec2, c: Vec2): boolean {
  return (b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1]) > 0;
}

// The mesh stores positions in single precision. A sliver only a fraction of a millimetre high, halved
// at a midpoint that rounds off its edge, can come out with a half wound the other way, facing down.
function halvesFaceUp(work: Work, a: number, b: number): boolean {
  const key = edgeKey(a, b);
  const m: Vec2 = [
    Math.fround((work.positions[a * 2]! + work.positions[b * 2]!) / 2),
    Math.fround((work.positions[a * 2 + 1]! + work.positions[b * 2 + 1]!) / 2),
  ];
  return (work.edges.get(key) ?? []).every((t) => {
    const [p, q, r] = aroundEdge(work, t, key).map((v) => singlePrecisionAt(work, v));
    return facesUp(p!, m, r!) && facesUp(m, q!, r!);
  });
}

// A sliver too flat to halve covers no ground, so it goes. On the outline its other two edges become the
// outline. Inside, the other diagonal of the two triangles on the edge replaces it, which leaves two
// triangles where the sliver and its neighbour were; null where that diagonal would not face up.
function dropSliverAt(work: Work, a: number, b: number): readonly number[] | null {
  const key = edgeKey(a, b);
  const sharing = [...(work.edges.get(key) ?? [])];
  if (sharing.length === 1) {
    retireTriangle(work, sharing[0]!);
    return [];
  }
  if (sharing.length !== 2) return null;
  const [p, q, r1] = aroundEdge(work, sharing[0]!, key);
  const r2 = aroundEdge(work, sharing[1]!, key)[2];
  const [pAt, qAt, r1At, r2At] = [p, q, r1, r2].map((v) => singlePrecisionAt(work, v));
  if (!facesUp(pAt!, r2At!, r1At!) || !facesUp(r2At!, qAt!, r1At!)) return null;
  for (const t of sharing) retireTriangle(work, t);
  return [addTriangle(work, [p, r2, r1]), addTriangle(work, [r2, q, r1])];
}

// The end of the path of ever longer edges from a triangle: an edge longest in both triangles on it,
// or on the outline.
function terminalEdge(work: Work, t: number): Edge {
  let current = t;
  for (;;) {
    const [a, b] = longestEdge(work, current);
    const next = work.edges.get(edgeKey(a, b))?.find((other) => other !== current);
    if (next === undefined || edgeKey(...longestEdge(work, next)) === edgeKey(a, b)) return [a, b];
    current = next;
  }
}

// A swapped diagonal can leave another sliver on the path; this bounds how many one path removes.
const MAX_SLIVERS_PER_PATH = 8;

// Rivara's longest-edge propagation: bisect the terminal edge of the path of ever longer edges until
// the triangle itself is split, which keeps the mesh conforming and its angles from collapsing.
function refineAlongLepp(work: Work, t: number): readonly number[] {
  const created: number[] = [];
  let slivers = 0;
  while (work.alive[t]) {
    const [a, b] = terminalEdge(work, t);
    const halved = halvesFaceUp(work, a, b);
    if (!halved && slivers++ === MAX_SLIVERS_PER_PATH) break;
    const made = halved ? bisectEdge(work, a, b) : dropSliverAt(work, a, b);
    if (made === null) break;
    created.push(...made);
  }
  return created;
}

// A colour blends only along an edge whose ends carry different labels, so only those edges need to
// be short; the edge between two corners of one label may stay longer.
function isMixed(work: Work, t: number, minEdge: number): boolean {
  if (!work.alive[t]) return false;
  const [a, b, c] = cornersOf(work, t);
  const [la, lb, lc] = [work.labels[a] ?? null, work.labels[b] ?? null, work.labels[c] ?? null];
  if (la === null || lb === null || lc === null) return false;
  const longest = minEdge * minEdge;
  return (
    (la !== lb && lengthSq(work, a, b) > longest) ||
    (lb !== lc && lengthSq(work, b, c) > longest) ||
    (lc !== la && lengthSq(work, c, a) > longest)
  );
}

function createWork(input: Triangulation, labeller: VertexLabeller): Work {
  const work: Work = {
    positions: Array.from(input.positions),
    corners: [],
    alive: [],
    edges: new Map(),
    labels: [],
    labeller,
  };
  for (let i = 0; i < input.indices.length; i += 3) {
    addTriangle(work, [input.indices[i]!, input.indices[i + 1]!, input.indices[i + 2]!]);
  }
  const probes = clearestCornerProbes(
    (v) => pointAt(work, v),
    input.indices,
    () => true,
    labeller.reach
  );
  for (const [v, probe] of probes) work.labels[v] = labeller.labelAt(pointAt(work, v), probe);
  return work;
}

// Splits triangles until no edge between two different labels is longer than `minEdge`, so a colour
// interpolated between labels changes within that distance across their boundary.
export function refineLabelBoundaries(input: Triangulation, rule: LabelBoundaries): Triangulation {
  const work = createWork(input, rule.labeller);
  const queue: number[] = [];
  for (let t = 0; t < work.alive.length; t++) if (isMixed(work, t, rule.minEdge)) queue.push(t);
  for (let next = 0; next < queue.length; next++) {
    if (!isMixed(work, queue[next]!, rule.minEdge)) continue;
    for (const t of refineAlongLepp(work, queue[next]!)) {
      if (isMixed(work, t, rule.minEdge)) queue.push(t);
    }
  }
  const indices = work.alive.flatMap((alive, t) => (alive ? cornersOf(work, t) : []));
  return { positions: Float64Array.from(work.positions), indices: Uint32Array.from(indices) };
}
